import React, { createContext, useContext, useEffect, useMemo, useRef, useState, useCallback } from "react";
import { api } from "@/lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    try {
      const r = await api.get("/auth/me");
      setUser(r.data);
    } catch (e) {
      // 401 on first load is expected before login — surface others.
      if (e?.response?.status && e.response.status !== 401) {
        console.warn("[auth] /auth/me failed:", e.response.status);
      }
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (window.location.hash?.includes("session_id=")) {
      setLoading(false);
      return;
    }
    checkAuth();
  }, [checkAuth]);

  const tzReportedRef = useRef(false);
  useEffect(() => {
    if (!user || tzReportedRef.current) return;
    try {
      const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
      if (tz && tz !== user.timezone) {
        tzReportedRef.current = true;
        api.patch("/auth/timezone", { timezone: tz }).catch((err) => {
          tzReportedRef.current = false;
          console.warn("[auth] tz PATCH failed:", err?.response?.status || err?.message);
        });
      } else {
        tzReportedRef.current = true;
      }
    } catch (e) {
      console.warn("[auth] tz detection failed:", e?.message);
    }
  }, [user]);

  const loginEmail = useCallback(async (email, password) => {
    const r = await api.post("/auth/login", { email, password });
    localStorage.setItem("fslab_session_token", r.data.session_token);
    setUser(r.data.user);
    return r.data.user;
  }, []);

  const registerEmail = useCallback(async (email, password, name) => {
    const r = await api.post("/auth/register", { email, password, name });
    localStorage.setItem("fslab_session_token", r.data.session_token);
    setUser(r.data.user);
    return r.data.user;
  }, []);

  const exchangeEmergentSession = useCallback(async (session_id) => {
    const r = await api.post("/auth/emergent-session", { session_id });
    localStorage.setItem("fslab_session_token", r.data.session_token);
    setUser(r.data.user);
    return r.data.user;
  }, []);

  const logout = useCallback(async () => {
    try {
      await api.post("/auth/logout");
    } catch (e) {
      console.warn("[auth] logout endpoint failed (clearing local state anyway):", e?.message);
    }
    localStorage.removeItem("fslab_session_token");
    setUser(null);
  }, []);

  const value = useMemo(
    () => ({ user, loading, loginEmail, registerEmail, exchangeEmergentSession, logout, refresh: checkAuth }),
    [user, loading, loginEmail, registerEmail, exchangeEmergentSession, logout, checkAuth],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export const useAuth = () => useContext(AuthContext);
